import React, { useState } from 'react';
import { Plus, Trash2, Copy, Anchor, Ship, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { PageSection } from '../../../../types';
import { NumberStepper } from '../common/NumberStepper';

interface YachtFleetEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const YachtFleetEditor: React.FC<YachtFleetEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};
  const yachts: any[] = content.yachts || content.items || [
    {
      id: 'y1',
      name: 'Azimut Grande 35M Lüks Motoryat',
      type: 'Motor Yacht',
      lengthMeters: 35,
      capacity: 10,
      cabins: 5,
      pricePerDay: 4500,
      currency: 'EUR',
      portLocation: 'Göcek',
      coverImage: 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d17?q=80&w=800&auto=format&fit=crop',
    },
    {
      id: 'y2',
      name: 'Custom Deluxe Gulet 32M',
      type: 'Gulet',
      lengthMeters: 32,
      capacity: 12,
      cabins: 6,
      pricePerDay: 2800,
      currency: 'EUR',
      portLocation: 'Bodrum',
      coverImage: 'https://images.unsplash.com/photo-1540946485063-a40da27545f8?q=80&w=800&auto=format&fit=crop',
    },
  ];

  const [activeYachtId, setActiveYachtId] = useState<string | null>(yachts[0]?.id || null);

  const handleUpdateYacht = (index: number, updates: any) => {
    const next = [...yachts];
    next[index] = { ...next[index], ...updates };
    onUpdateContent({ yachts: next, items: next });
  };

  const handleAddYacht = () => {
    const newYacht = {
      id: `yacht_${Date.now().toString(36)}`,
      name: `Yeni Lüks Tekne ${yachts.length + 1}`,
      type: 'Motor Yacht',
      lengthMeters: 28,
      capacity: 8,
      cabins: 4,
      pricePerDay: 3000,
      currency: 'EUR',
      portLocation: 'Göcek',
      coverImage: 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?q=80&w=800&auto=format&fit=crop',
    };
    const next = [...yachts, newYacht];
    onUpdateContent({ yachts: next, items: next });
    setActiveYachtId(newYacht.id);
  };

  const handleDuplicateYacht = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const source = yachts[index];
    const duplicated = {
      ...JSON.parse(JSON.stringify(source)),
      id: `yacht_${Date.now().toString(36)}`,
      name: `${source.name} (Kopya)`,
    };
    const next = [...yachts];
    next.splice(index + 1, 0, duplicated);
    onUpdateContent({ yachts: next, items: next });
    setActiveYachtId(duplicated.id);
  };

  const handleDeleteYacht = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = yachts.filter((_, i) => i !== index);
    onUpdateContent({ yachts: next, items: next });
    if (activeYachtId === yachts[index]?.id) {
      setActiveYachtId(next[0]?.id || null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Headings */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Filo Başlıkları</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge ?? 'LÜKS YAT VE GULET FİLOSU'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title ?? 'Masmavi Bir Seyir İçin Seçkin Teknelerimiz'}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama</label>
          <textarea
            value={content.description ?? ''}
            onChange={(e) => onUpdateContent({ description: e.target.value })}
            rows={2}
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
          />
        </div>
      </div>

      {/* Yachts Accordion */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Anchor className="w-4 h-4 text-cyan-400" />
            <h4 className="text-xs font-extrabold text-slate-200">Tekne Listesi ({yachts.length})</h4>
          </div>
          <button
            type="button"
            onClick={handleAddYacht}
            className="px-2.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold flex items-center gap-1.5 shadow-sm transition"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Tekne Ekle</span>
          </button>
        </div>

        <div className="space-y-2.5">
          {yachts.map((y, idx) => {
            const isExpanded = activeYachtId === y.id;

            return (
              <div
                key={y.id || idx}
                className={`rounded-2xl border transition-all ${
                  isExpanded
                    ? 'bg-slate-800/90 border-cyan-500/50 shadow-md ring-1 ring-cyan-500/20'
                    : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600'
                }`}
              >
                {/* Header */}
                <div
                  onClick={() => setActiveYachtId(isExpanded ? null : y.id)}
                  className="p-3 flex items-center justify-between cursor-pointer select-none"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <img
                      src={y.coverImage}
                      alt={y.name}
                      className="w-8 h-8 rounded-lg object-cover bg-slate-900 border border-slate-700 shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-200 truncate">{y.name}</div>
                      <div className="text-[10px] text-slate-400">
                        {y.type} · €{(y.pricePerDay || 0).toLocaleString()} / gün
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => handleDuplicateYacht(idx, e)}
                      title="Kopyala"
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteYacht(idx, e)}
                      title="Sil"
                      className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="p-1 text-slate-400">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                </div>

                {/* Expanded */}
                {isExpanded && (
                  <div className="p-4 pt-2 border-t border-slate-700/50 space-y-3.5">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Tekne Adı</label>
                      <input
                        type="text"
                        value={y.name || ''}
                        onChange={(e) => handleUpdateYacht(idx, { name: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-cyan-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Tekne Tipi</label>
                        <select
                          value={y.type || 'Motor Yacht'}
                          onChange={(e) => handleUpdateYacht(idx, { type: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-cyan-500"
                        >
                          <option value="Motor Yacht">Motor Yacht</option>
                          <option value="Gulet">Gulet</option>
                          <option value="Catamaran">Catamaran</option>
                          <option value="Sailing">Yelkenli</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Liman / Lokasyon</label>
                        <input
                          type="text"
                          value={y.portLocation || 'Göcek'}
                          onChange={(e) => handleUpdateYacht(idx, { portLocation: e.target.value })}
                          placeholder="Göcek / Bodrum"
                          className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-cyan-500"
                        />
                      </div>
                    </div>

                    {/* Specs */}
                    <div className="grid grid-cols-3 gap-2">
                      <NumberStepper
                        label="Uzunluk (m)"
                        value={y.lengthMeters || 30}
                        min={5}
                        max={150}
                        onChange={(val) => handleUpdateYacht(idx, { lengthMeters: val })}
                      />
                      <NumberStepper
                        label="Kapasite"
                        value={y.capacity || 10}
                        min={1}
                        max={50}
                        onChange={(val) => handleUpdateYacht(idx, { capacity: val })}
                      />
                      <NumberStepper
                        label="Kabin"
                        value={y.cabins || 4}
                        min={1}
                        max={20}
                        onChange={(val) => handleUpdateYacht(idx, { cabins: val })}
                      />
                    </div>

                    {/* Pricing */}
                    <NumberStepper
                      label="Günlük Fiyat (€ EUR)"
                      value={y.pricePerDay || 2500}
                      step={100}
                      min={0}
                      onChange={(val) => handleUpdateYacht(idx, { pricePerDay: val, currency: 'EUR' })}
                    />

                    {/* Cover Image */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Görsel URL</label>
                      <input
                        type="url"
                        value={y.coverImage || ''}
                        onChange={(e) => handleUpdateYacht(idx, { coverImage: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
